import React, { FC, useEffect } from 'react';

import { Box } from '@mui/material';
import { styled } from '@mui/material';
import { useClearRefinements, useHierarchicalMenu } from 'react-instantsearch';
import { useParams } from 'react-router-dom';

import AnimatedSkeleton from '~/components/AnimatedSkeleton';
import { useDiscoverContext } from '~/context/DiscoverContext';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import CategoryNavigationLink from '~/pages/DiscoverPage/components/CategoryNavigationLink';
import { buildDiscoverRoute, DISCOVER_SEARCH_ROUTE } from '~/routes';
import { SKELETON_TEXT_HEIGHT_SMALL_PX, SMALL_MARGIN_PX } from '~/theme';

interface CategoryNavigationBlockProps {
  attributes: string[];
  loading?: boolean;
  initialCategory?: string;
}

export const CategoryNavigationBlock: FC<CategoryNavigationBlockProps> = ({
  attributes,
  loading,
  initialCategory,
}) => {
  const { t } = useTenantTranslation();
  const params = useParams();
  const { categoryTitle } = useDiscoverContext();

  const { category, subcategory } = params;

  const { refine } = useHierarchicalMenu({ attributes });
  const { refine: clearRefinement } = useClearRefinements();

  useEffect(() => {
    if (refine && initialCategory) {
      clearRefinement();
      refine(initialCategory);
    }
  }, [refine, initialCategory, clearRefinement]);

  return (
    <Wrapper>
      {subcategory && loading && <LoadingSubcategory xs={SKELETON_TEXT_HEIGHT_SMALL_PX} />}
      {subcategory && !loading && (
        <CategoryNavigationLink data-testid="backToRootCategory" to={buildDiscoverRoute(category)}>
          {t('page.discover.backTo', { category: categoryTitle })}
        </CategoryNavigationLink>
      )}
      <CategoryNavigationLink data-testid="backToAllCategories" to={DISCOVER_SEARCH_ROUTE}>
        {t('page.discover.backToLanding')}
      </CategoryNavigationLink>
    </Wrapper>
  );
};

export default CategoryNavigationBlock;

const Wrapper = styled(Box)`
  & a {
    margin-bottom: ${SMALL_MARGIN_PX};

    &:last-child {
      margin-bottom: 0;
    }
  }
`;
const LoadingSubcategory = styled(AnimatedSkeleton)`
  margin-bottom: ${SMALL_MARGIN_PX};

  &:last-child {
    margin-bottom: 0;
  }
`;
